import { useState, useEffect, useCallback } from 'react';
import useTabState from '../hooks/useTabState';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import api from '../api';

const TABS = ['Overview', 'Agents', 'Learning Loop', 'Workflows', 'Library', 'Thresholds'] as const;
type Tab = typeof TABS[number];

/* --- Shared Helpers --- */

function scoreColor(score: number): string {
  return score > 80 ? 'var(--green)' : score > 50 ? 'var(--amber)' : 'var(--red)';
}

function SeverityBadge({ severity }: { severity: string }) {
  const s = (severity ?? '').toUpperCase();
  const color = s === 'CRITICAL' ? 'var(--red)' : s === 'HIGH' ? 'var(--amber)' : s === 'MEDIUM' ? 'var(--blue)' : 'var(--text-tertiary)';
  const bg = s === 'CRITICAL' ? '#fef2f2' : s === 'HIGH' ? '#fffbeb' : s === 'MEDIUM' ? 'var(--accent-subtle)' : 'var(--bg-elevated)';
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: bg, color }}>{s || 'INFO'}</span>
  );
}

function ErrorMsg({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <p style={{ color: 'var(--red)', padding: 20 }}>
      {message}{' '}
      <span onClick={onRetry} style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}>Retry</span>
    </p>
  );
}

/* --- Main Component --- */

export default function PlatformIntelligence() {
  const [activeTab, setActiveTab] = useTabState<Tab>('tab', TABS, 'Overview');

  return (
    <PageLayout title="Platform Intelligence" subtitle="Self-audit health, meta-agent oversight, learning loop, and operational diagnostics">
      <TabBar
        tabs={TABS.map(t => ({ key: t, label: t }))}
        active={activeTab}
        onChange={v => setActiveTab(v as Tab)}
      />
      {activeTab === 'Overview' && <OverviewTab />}
      {activeTab === 'Agents' && <AgentsTab />}
      {activeTab === 'Learning Loop' && <LearningLoopTab />}
      {activeTab === 'Workflows' && <WorkflowsTab />}
      {activeTab === 'Library' && <LibraryTab />}
      {activeTab === 'Thresholds' && <ThresholdsTab />}
    </PageLayout>
  );
}

/* --- Tab 1: Overview --- */

function OverviewTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audit, setAudit] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/diagnostics/self-audit/latest').then(r => r.data).catch(() => null),
      api.get('/diagnostics/meta-agents/agents').then(r => r.data).catch(() => []),
      api.get('/diagnostics/self-audit/history?periods=10').then(r => r.data).catch(() => ({ snapshots: [] })),
    ]).then(([a, ag, h]) => {
      setAudit(a);
      setAgents(Array.isArray(ag) ? ag : ag?.agents ?? []);
      setHistory(h?.snapshots ?? (Array.isArray(h) ? h : []));
    }).catch(() => setError('Unable to load overview data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>Loading...</p>;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  const metrics = audit?.metrics_json ?? {};
  const rawHealth = metrics.health_score;
  const healthScore = typeof rawHealth === 'number' ? rawHealth
    : typeof audit?.severity_score === 'number' ? Math.round((1 - audit.severity_score) * 100)
    : null;
  const findings: any[] = audit?.findings ?? metrics?.findings ?? [];
  const criticalCount = findings.filter((f: any) => (f.severity ?? '').toUpperCase() === 'CRITICAL').length;
  const executionMs = metrics.execution_ms ?? audit?.execution_ms ?? null;
  const activeAgents = agents.filter((a: any) => a.is_active).length;
  const lastCycle = audit?.created_at ?? audit?.snapshot_date ?? null;

  const findingCols: Column[] = [
    { key: 'severity', label: 'Severity', render: (_: any, row: any) => <SeverityBadge severity={row.severity ?? row.level ?? ''} /> },
    { key: 'finding', label: 'Finding', render: (_: any, row: any) => row.finding ?? row.title ?? row.description ?? '--' },
    { key: 'department', label: 'Department', render: (_: any, row: any) => row.department ?? row.dept ?? '--' },
    { key: 'recommended_action', label: 'Recommended Action', render: (_: any, row: any) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{row.recommended_action ?? row.recommendation ?? '--'}</span> },
  ];

  return (
    <div>
      <KpiGrid columns={4} items={[
        { label: 'Health Score', value: healthScore != null ? healthScore : '--', color: healthScore != null ? scoreColor(healthScore) : 'var(--text-tertiary)' },
        { label: 'Agents', value: `${activeAgents}/${agents.length} Active`, color: 'var(--accent)', subtitle: lastCycle ? `Last cycle: ${new Date(lastCycle).toLocaleDateString()}` : undefined },
        { label: 'Findings', value: findings.length, subtitle: criticalCount > 0 ? `${criticalCount} CRITICAL` : undefined },
        { label: 'Cycle Time', value: executionMs != null ? (executionMs / 1000).toFixed(1) + 's' : '--' },
      ]} />

      {/* Health Score Trend */}
      {history.length > 0 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Health Score Trend</div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 120, marginTop: 8 }}>
            {history.map((h: any, i: number) => {
              const raw = h.metrics_json?.health_score ?? h.severity_score ?? 0;
              const normalized = Math.max(0, Math.min(100, typeof raw === 'number' ? raw : 0));
              const color = scoreColor(normalized);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{normalized}</span>
                  <div style={{ width: '100%', height: `${normalized}%`, background: color, borderRadius: 3, minHeight: 2 }} />
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Findings table */}
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Findings</div>
      <DataTable columns={findingCols} rows={findings} emptyMessage="No findings in latest audit." />
    </div>
  );
}

/* --- Tab 2: Agents --- */

function AgentsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState<any[]>([]);
  const [cycleRunning, setCycleRunning] = useState(false);
  const [cycleResult, setCycleResult] = useState<any>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/diagnostics/meta-agents/agents')
      .then(r => setAgents(Array.isArray(r.data) ? r.data : r.data?.agents ?? []))
      .catch(() => setError('Unable to load agents.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRunCycle = useCallback(() => {
    setCycleRunning(true);
    setCycleResult(null);
    api.post('/diagnostics/meta-agents/cycle')
      .then(r => setCycleResult(r.data))
      .catch(() => setCycleResult({ error: 'Cycle failed' }))
      .finally(() => setCycleRunning(false));
  }, []);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>Loading...</p>;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{agents.length} registered agents</div>
        <ActionButton label={cycleRunning ? 'Running...' : 'Run Full Cycle'} onClick={handleRunCycle} disabled={cycleRunning} />
      </div>

      {/* Cycle result */}
      {cycleResult && (
        <div style={{
          background: cycleResult.error ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
          border: `1px solid ${cycleResult.error ? 'var(--red)' : 'var(--green)'}`,
          borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 12,
        }}>
          {cycleResult.error ? (
            <span style={{ color: 'var(--red)' }}>{cycleResult.error}</span>
          ) : (
            <div>
              <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>Cycle complete</div>
              {Array.isArray(cycleResult.results) && cycleResult.results.map((r: any, i: number) => {
                const status = r.status ?? 'done';
                const color = status === 'COMPLETED' || status === 'success' || status === 'ok'
                  ? 'var(--green)'
                  : status === 'SKIPPED'
                    ? 'var(--amber)'
                    : 'var(--red)';
                return (
                  <div key={i} style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.agent_code ?? r.agent ?? `Agent ${i + 1}`}</span>
                    {' '}&mdash;{' '}
                    <span style={{ color }}>{status}</span>
                    {r.reason && <span style={{ color: 'var(--text-tertiary)' }}> &mdash; {r.reason}</span>}
                    {r.message && <span> &mdash; {r.message}</span>}
                    {typeof r.severity_score === 'number' && r.severity_score > 0 && (
                      <span style={{ color: 'var(--text-tertiary)' }}> &middot; severity {r.severity_score.toFixed(2)}</span>
                    )}
                  </div>
                );
              })}
              {typeof cycleResult === 'object' && !Array.isArray(cycleResult.results) && (
                <pre style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0 }}>
                  {JSON.stringify(cycleResult, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Agent cards */}
      {agents.length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>No agents registered yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 }}>
          {agents.map((a: any) => (
            <div key={a.agent_code ?? a.id} style={{
              background: 'var(--bg-card)', borderRadius: 10, padding: 20,
              border: '1px solid var(--border-subtle)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{a.agent_name ?? a.name ?? a.agent_code}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {a.agent_code} | {a.hierarchy_level ?? '--'}
                  </div>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                  background: a.hierarchy_level === 'L6' ? '#fef2f2' : 'var(--accent-border)',
                  color: a.hierarchy_level === 'L6' ? 'var(--red)' : 'var(--accent)',
                }}>{a.hierarchy_level ?? '--'}</span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
                {(a.agent_role ?? a.role ?? '').slice(0, 200)}{(a.agent_role ?? a.role ?? '').length > 200 ? '...' : ''}
              </p>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Schedule: {a.schedule ?? '--'}</span>
                <span style={{ fontSize: 11, color: a.is_active ? 'var(--green)' : 'var(--red)' }}>
                  {a.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --- Tab 3: Learning Loop --- */

function LearningLoopTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trend, setTrend] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/diagnostics/learning-loop/trend').then(r => r.data).catch(() => []),
      api.get('/diagnostics/learning-loop/promotion-candidates').then(r => r.data).catch(() => []),
    ]).then(([t, c]) => {
      setTrend(Array.isArray(t) ? t : t?.trend ?? t?.periods ?? []);
      setCandidates(Array.isArray(c) ? c : c?.candidates ?? c?.promotion_candidates ?? []);
    }).catch(() => setError('Unable to load learning loop data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>Loading...</p>;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  if (trend.length === 0 && candidates.length === 0) {
    return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>No learning loop data yet. This section activates after resolution outcomes are captured.</p>;
  }

  const trendCols: Column[] = [
    { key: 'period', label: 'Period', render: (_: any, row: any) => row.period ?? row.month ?? '--' },
    { key: 'approval_rate', label: 'Approval Rate', render: (_: any, row: any) => <span style={{ color: 'var(--green)', fontVariantNumeric: 'tabular-nums' }}>{row.approval_rate != null ? (row.approval_rate * 100).toFixed(1) + '%' : '--'}</span> },
    { key: 'modification_rate', label: 'Modification Rate', render: (_: any, row: any) => <span style={{ color: 'var(--amber)', fontVariantNumeric: 'tabular-nums' }}>{row.modification_rate != null ? (row.modification_rate * 100).toFixed(1) + '%' : '--'}</span> },
    { key: 'total', label: 'Total', render: (_: any, row: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.total_count ?? row.total ?? '--'}</span> },
  ];

  const candidateCols: Column[] = [
    { key: 'exception_type', label: 'Exception Type' },
    { key: 'consecutive_approvals', label: 'Consecutive Approvals', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v ?? '--'}</span> },
    { key: 'avg_confidence', label: 'Avg Confidence', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--green)' }}>{v != null ? (v * 100).toFixed(0) + '%' : '--'}</span> },
    { key: 'framework', label: 'Framework' },
  ];

  return (
    <div>
      {trend.length > 0 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Outcome Trend</div>
          <DataTable columns={trendCols} rows={trend} />
        </>
      )}
      {candidates.length > 0 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Promotion Candidates</div>
          <DataTable columns={candidateCols} rows={candidates} />
        </>
      )}
    </div>
  );
}

/* --- Tab 4: Workflows --- */

function WorkflowsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [perf, setPerf] = useState<any[]>([]);
  const [bottlenecks, setBottlenecks] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/diagnostics/workflow-mining/performance').then(r => r.data).catch(() => []),
      api.get('/diagnostics/workflow-mining/bottlenecks').then(r => r.data).catch(() => []),
    ]).then(([p, b]) => {
      setPerf(Array.isArray(p) ? p : p?.performance ?? p?.workflows ?? []);
      setBottlenecks(Array.isArray(b) ? b : b?.bottlenecks ?? []);
    }).catch(() => setError('Unable to load workflow data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>Loading...</p>;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  const failColor = (rate: number | null) => {
    if (rate == null) return 'var(--text-primary)';
    const pct = rate > 1 ? rate : rate * 100;
    return pct > 15 ? 'var(--red)' : pct > 5 ? 'var(--amber)' : 'var(--green)';
  };

  const perfCols: Column[] = [
    { key: 'workflow_code', label: 'Workflow', render: (v: any) => <span style={{ fontWeight: 600 }}>{v ?? '--'}</span> },
    { key: 'total_runs', label: 'Total Runs', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v ?? '--'}</span> },
    { key: 'avg_execution_ms', label: 'Avg Execution', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v != null ? (v / 1000).toFixed(2) + 's' : '--'}</span> },
    { key: 'success_rate', label: 'Success Rate', render: (v: any) => { const pct = v != null ? (v > 1 ? v : v * 100) : null; return <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--green)' }}>{pct != null ? pct.toFixed(1) + '%' : '--'}</span>; } },
    { key: 'failure_rate', label: 'Failure Rate', render: (v: any) => { const pct = v != null ? (v > 1 ? v : v * 100) : null; return <span style={{ fontVariantNumeric: 'tabular-nums', color: failColor(v) }}>{pct != null ? pct.toFixed(1) + '%' : '--'}</span>; } },
  ];

  const bottleneckCols: Column[] = [
    { key: 'workflow_code', label: 'Workflow', render: (v: any) => <span style={{ fontWeight: 600 }}>{v ?? '--'}</span> },
    { key: 'step_number', label: 'Step', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v ?? '--'}</span> },
    { key: 'skill_code', label: 'Skill' },
    { key: 'avg_step_ms', label: 'Avg Step Time', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v != null ? (v / 1000).toFixed(2) + 's' : '--'}</span> },
    { key: 'ratio', label: 'Ratio', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--amber)' }}>{v != null ? (typeof v === 'number' ? (v * 100).toFixed(0) + '%' : v) : '--'}</span> },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Workflow Performance</div>
      <DataTable columns={perfCols} rows={perf} emptyMessage="No workflow performance data available yet." />

      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Bottlenecks</div>
      <DataTable columns={bottleneckCols} rows={bottlenecks} emptyMessage="No bottleneck data available yet." />
    </div>
  );
}

/* --- Tab 5: Library --- */

function LibraryTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cited, setCited] = useState<any[]>([]);
  const [stale, setStale] = useState<any>(null);
  const [distribution, setDistribution] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/diagnostics/library/most-cited?limit=15').then(r => r.data).catch(() => []),
      api.get('/diagnostics/library/stale').then(r => r.data).catch(() => null),
      api.get('/diagnostics/library/distribution').then(r => r.data).catch(() => []),
    ]).then(([c, s, d]) => {
      setCited(Array.isArray(c) ? c : c?.entries ?? []);
      setStale(s);
      if (d?.ranges && typeof d.ranges === 'object') {
        setDistribution(Object.entries(d.ranges).map(([label, count]: any) => ({ range: label, count })));
      } else {
        setDistribution(Array.isArray(d) ? d : d?.distribution ?? []);
      }
    }).catch(() => setError('Unable to load library data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>Loading...</p>;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  const staleEntries: any[] = stale?.entries ?? (Array.isArray(stale) ? stale : []);
  const staleCount = stale?.count ?? staleEntries.length;
  const maxCitation = Math.max(1, ...distribution.map((d: any) => d.count ?? d.value ?? 0));

  const citedCols: Column[] = [
    { key: 'document_code', label: 'Document', render: (v: any) => <span style={{ fontWeight: 600 }}>{v ?? '--'}</span> },
    { key: 'section_reference', label: 'Section' },
    { key: 'exception_type', label: 'Exception Type' },
    { key: 'framework', label: 'Framework' },
    { key: 'citation_count', label: 'Citations', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--accent)' }}>{v ?? '--'}</span> },
  ];

  const staleCols: Column[] = [
    { key: 'document_code', label: 'Document' },
    { key: 'section_reference', label: 'Section' },
    { key: 'last_used', label: 'Last Used', render: (v: any) => v ? new Date(v).toLocaleDateString() : '--' },
    { key: 'age_days', label: 'Age (days)', render: (v: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v ?? '--'}</span> },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Most Cited References</div>
      <DataTable columns={citedCols} rows={cited} emptyMessage="No citation data available yet." />

      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>
        Stale Entries {staleCount > 0 && <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--amber)' }}>({staleCount})</span>}
      </div>
      <DataTable columns={staleCols} rows={staleEntries.slice(0, 10)} emptyMessage="No stale entries detected." />

      {/* Distribution */}
      {distribution.length > 0 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Citation Distribution</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            {distribution.map((d: any, i: number) => {
              const count = d.count ?? d.value ?? 0;
              const pct = (count / maxCitation) * 100;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 120, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', flexShrink: 0 }}>
                    {d.range ?? d.label ?? d.bucket ?? `Range ${i + 1}`}
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 3, height: 18, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 3, minWidth: count > 0 ? 4 : 0 }} />
                  </div>
                  <div style={{ width: 40, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)' }}>
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* --- Tab 6: Thresholds --- */

function ThresholdsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [thresholds, setThresholds] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/thresholds').then(r => r.data).catch(() => []),
      api.get('/thresholds/suggestions').then(r => r.data).catch(() => []),
    ]).then(([t, s]) => {
      setThresholds(Array.isArray(t) ? t : t?.thresholds ?? []);
      setSuggestions(Array.isArray(s) ? s : s?.suggestions ?? []);
    }).catch(() => setError('Unable to load threshold data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 40 }}>Loading...</p>;
  if (error) return <ErrorMsg message={error} onRetry={load} />;

  const thresholdCols: Column[] = [
    { key: 'type', label: 'Type', render: (_: any, row: any) => <span style={{ fontWeight: 600 }}>{row.threshold_type ?? row.type ?? '--'}</span> },
    { key: 'value', label: 'Current Value', render: (_: any, row: any) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--accent)' }}>{row.current_value ?? row.value ?? '--'}</span> },
    { key: 'unit', label: 'Unit', render: (_: any, row: any) => row.value_unit ?? row.unit ?? '--' },
    { key: 'min', label: 'Min Allowed', render: (_: any, row: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.min_allowed ?? '--'}</span> },
    { key: 'max', label: 'Max Allowed', render: (_: any, row: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.max_allowed ?? '--'}</span> },
  ];

  const suggestionCols: Column[] = [
    { key: 'type', label: 'Type', render: (_: any, row: any) => <span style={{ fontWeight: 600 }}>{row.threshold_type ?? row.type ?? '--'}</span> },
    { key: 'current', label: 'Current', render: (_: any, row: any) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.current_value ?? '--'}</span> },
    { key: 'suggested', label: 'Suggested', render: (_: any, row: any) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--amber)' }}>{row.suggested_value ?? '--'}</span> },
    { key: 'reason', label: 'Reason', render: (_: any, row: any) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{row.suggestion_reason ?? row.reason ?? '--'}</span> },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Current Thresholds</div>
      <DataTable columns={thresholdCols} rows={thresholds} emptyMessage="No thresholds configured yet." />

      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '28px 0 12px' }}>Pending Suggestions</div>
      <DataTable columns={suggestionCols} rows={suggestions} emptyMessage="No threshold suggestions pending." />
    </div>
  );
}
